import type {
  ApiItemType,
  ApiReturnProject,
  ApiReturnSite,
} from '@highimpact-frontend/api-types';

import type { DashboardHostApi } from '../api/DashboardHostApi';
import type { DashboardGridType, DashboardScope } from '../state/dashboardRuntimeTypes';
import type {
  FederationHostApi,
  RemoteWidgetRenderFn,
} from './remoteTypes';

// -- optional host capabilities --

export type DashboardNotificationSeverity = 'success' | 'error' | 'info';

export interface CreateReportFormProps {
  readonly onDashboardCreated: (info: {
    dashboardName: string;
    dashboard_id: number;
    site_id: number;
    project_id: number;
  }) => void;
  readonly onCancel?: () => void;
}

export interface DashboardReportHostCapabilities {
  /** When false, report create/export controls are hidden. Defaults to true. */
  readonly enabled?: boolean;
}

// -- host context value --

/** How the dashboard is opened inside the host application. */
export type DashboardHostMode = 'site' | 'standalone' | 'public';

/**
 * Everything the host app must provide so the dashboard library.
 */
export interface DashboardHostContextValue {
  readonly project_id: number | null;
  readonly site_id: number | null;
  readonly grid_id: number | null;
  readonly project?: ApiReturnProject | null;
  readonly site?: ApiReturnSite | null;
  readonly mode: DashboardHostMode;
  readonly grid_type: DashboardGridType;
  readonly simplified_view: boolean;
  readonly is_editable: boolean;
  readonly dashboard_only_mode: boolean;
  readonly navigate: (path: string) => void;
  readonly getAuthToken: () => string | null;
  readonly checkPermission: (
    item: ApiItemType,
    extra?: Record<string, unknown>
  ) => boolean;
  readonly buildDashboardUrl: (gridId: number) => string;
  readonly buildReportUrl: (gridId: number) => string;
  readonly api: DashboardHostApi;
  /** Host renders federated remote widgets (RemoteLoader stays in the host). */
  readonly renderRemoteWidget: RemoteWidgetRenderFn;
  readonly showNotification: (
    message: string,
    severity: DashboardNotificationSeverity
  ) => void;
  /** Optional report creation UI; when absent, lib hides report create controls. */
  readonly report?: DashboardReportHostCapabilities;
  /** Optional access to the host MF runtime singleton. */
  readonly federationHost?: FederationHostApi;
}

/** Builds a read-only scope object from the host context value. */
export const buildDashboardScopeFromHostContext = (
  hostContext: DashboardHostContextValue
): DashboardScope => ({
  project_id: hostContext.project_id,
  site_id: hostContext.site_id,
  grid_id: hostContext.grid_id,
});
